import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Table,
  Box,
  SpaceBetween,
  Pagination,
  TextFilter,
  CollectionPreferences,
  Header,
  Select
} from '@amzn/awsui-components-react/polaris';
import { apiCall } from '../../../utils';
import { getReleaseColumns } from './columns';
import { STATUS_OPTIONS } from './constants';
import PropTypes from 'prop-types';
import ElementErrorBoundary from '../../../components/ErrorBoundary/ElementErrorBoundary';
import { MetricsVisualization } from '../MetricsVisualization';
import { ErrorDisplay } from '../../../components/ErrorDisplay';
import { Alert } from "@amzn/awsui-components-react/polaris";
import { useParams } from 'react-router-dom';
import { getDomainIdentifier } from '../../../../utils/helpers';

export const SoftwareUpdateTrackerDetails = ({ stackName, isSDPDSMetric = false }) => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextToken, setNextToken] = useState(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filteringText, setFilteringText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('IN_PROGRESS');
  const [selectedRelease, setSelectedRelease] = useState(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const chartRef = useRef(null);
  const { clientId, domainName } = useParams();
  const domainIdentifier = getDomainIdentifier(clientId, domainName);

  const statusOptions = STATUS_OPTIONS.map(option => ({
    value: option.value,
    label: option.text || option.value
  }));

  const scrollToChart = useCallback(() => {
    setIsScrolling(true);
    if (chartRef.current) {
      const elementPosition = chartRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 100;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      setTimeout(() => {
        setIsScrolling(false);
      }, 500);
    }
  }, []);

  const handleReleaseSelect = useCallback((releaseVersion) => {
    setSelectedRelease(releaseVersion);
    setTimeout(scrollToChart, 100);
  }, []);

  const fetchReleases = async () => {
    if (!stackName) return;

    setItems([]);
    setIsLoading(true);

    let nextToken = null;
    const MAX_ITERATIONS = 50;

    for (let i = 0; i < MAX_ITERATIONS; i++) {
      try {
        const response = await apiCall('/external/getallReleases', {
          status: selectedStatus,
          stackName,
          maxResults: 50,
          ...(nextToken && { nextToken })
        });

        if (response.statusCode !== 200) {
          throw new Error(response.body?.message || 'Failed to fetch releases');
        }

        const newItems = response.body.stackReleaseSoftwareConfigList;
        setItems(prevItems => [...prevItems, ...newItems]);

        if (i === 0) setIsLoading(false);

        nextToken = response.body.nextToken;
        if (!nextToken) break;

      } catch (err) {
        setError(err.message || 'An error occurred while fetching releases');
        break;
      }
    }

    setIsLoading(false);
  };

  useEffect(() => {
    fetchReleases();
    setSelectedRelease(null);
  }, [selectedStatus, stackName]);

  const getFilteredItems = () => {
    if (!filteringText) return items;

    const searchText = filteringText.toLowerCase();
    return items.filter(item =>
      item.releaseVersion?.toLowerCase().includes(searchText) ||
      item.stackName?.toLowerCase().includes(searchText) ||
      item.deploymentId?.toLowerCase().includes(searchText)
    );
  };

  const metricsComponent = useMemo(() => {
    return selectedRelease ? (
      <MetricsVisualization
        releaseVersion={selectedRelease}
        stackName={stackName}
        isSDPDSMetric={isSDPDSMetric}
        domainIdentifier={domainIdentifier}
      />
    ) : null;
  }, [selectedRelease, stackName, isSDPDSMetric, domainIdentifier]);

  if (!items.length && isLoading) {
    return (
      <Box textAlign="center" padding={{ vertical: "l" }}>
        <SpaceBetween size="xs">
          <div>Loading...</div>
        </SpaceBetween>
      </Box>
    );
  }

  if (error) {
    return <ErrorDisplay />;
  }

  const filteredItems = getFilteredItems();
  const startIndex = (currentPageIndex - 1) * pageSize;
  const paginatedItems = filteredItems.slice(startIndex, startIndex + pageSize);

  return (
    <ElementErrorBoundary>
      <SpaceBetween size="l">
        <Alert
          type="info"
          statusIconAriaLabel="Info"
          header="Note"
          dismissible
        >
          Click on a release version to view release progress and cluster metrics for a release across the stack.
        </Alert>
        <Table
          columnDefinitions={getReleaseColumns(handleReleaseSelect)}
          items={paginatedItems}
          loading={isLoading}
          loadingText="Loading releases..."
          filter={
            <TextFilter
              filteringPlaceholder="Search releases..."
              filteringText={filteringText}
              onChange={({ detail }) => {
                setFilteringText(detail.filteringText);
                setCurrentPageIndex(1);
              }}
              countText={`${filteredItems.length} matches`}
            />
          }
          header={
            <Header
              counter={`(${filteredItems.length})`}
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Select
                    selectedOption={statusOptions.find(option => option.value === selectedStatus)}
                    onChange={({ detail }) => {
                      setSelectedStatus(detail.selectedOption.value);
                      setCurrentPageIndex(1);
                      setNextToken(null);
                    }}
                    options={statusOptions}
                    placeholder="Select status"
                  />
                </SpaceBetween>
              }
            >
              Releases
            </Header>
          }
          pagination={
            <Pagination
              currentPageIndex={currentPageIndex}
              onChange={({ detail }) => {
                setCurrentPageIndex(detail.currentPageIndex);
                if (
                  detail.currentPageIndex * pageSize > items.length &&
                  nextToken
                ) {
                  fetchReleases(nextToken);
                }
              }}
              pagesCount={Math.ceil(filteredItems.length / pageSize)}
              pageSize={pageSize}
            />
          }
          preferences={
            <CollectionPreferences
              title="Preferences"
              confirmLabel="Confirm"
              cancelLabel="Cancel"
              preferences={{ pageSize }}
              pageSizePreference={{
                title: 'Page size',
                options: [
                  { value: 10, label: '10 releases' },
                  { value: 20, label: '20 releases' },
                  { value: 50, label: '50 releases' },
                ],
              }}
              onConfirm={({ detail }) => {
                setPageSize(detail.pageSize);
                setCurrentPageIndex(1);
              }}
            />
          }
          empty={
            <Box textAlign="center" color="inherit">
              <b>No {selectedStatus.toLowerCase().replace('_', ' ')} releases found</b>
            </Box>
          }
          variant="embedded"
          stickyHeader={false}
        />
        {selectedRelease && (
          <div ref={chartRef} id="chart-section">
            {metricsComponent}
          </div>
        )}
      </SpaceBetween>
    </ElementErrorBoundary>
  );
};

SoftwareUpdateTrackerDetails.propTypes = {
  stackName: PropTypes.string.isRequired,
  isSDPDSMetric: PropTypes.bool
};
